/**
 * Plugin Manager component - initializes and manages plugins
 */

import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { TraceKitPlugin, AppContext } from '../types/plugins';
import { pluginRegistry } from './PluginRegistry';
import { useWorkspace } from '../hooks/useWorkspace';
import { getAPIClient } from '../api/client';

/** Context so AppContent can re-run sidebar useMemo after plugins are registered */
const PluginsReadyContext = createContext(false);
export function usePluginsReady(): boolean {
  return useContext(PluginsReadyContext);
}

export interface PluginManagerProps {
  plugins: TraceKitPlugin[];
  config: any;
  children: ReactNode;
}

export function PluginManager({ plugins, config, children }: PluginManagerProps) {
  const { projectId } = useWorkspace();
  const [pluginsReady, setPluginsReady] = useState(false);

  useEffect(() => {
    // Register all plugins on mount
    const context: AppContext = {
      tenantId: null,
      projectId: null,
      apiClient: getAPIClient(),
      config,
    };

    plugins.forEach((plugin) => {
      pluginRegistry.register(plugin);
      if (plugin.onInit) {
        plugin.onInit(context);
      }
    });
    setPluginsReady(true);

    return () => {
      setPluginsReady(false);
      // Unregister on unmount
      plugins.forEach((plugin) => {
        pluginRegistry.unregister(plugin.id);
      });
    };
  }, [plugins, config]);

  useEffect(() => {
    // Notify plugins of project change
    if (projectId) {
      plugins.forEach((plugin) => {
        if (plugin.onProjectChange) {
          plugin.onProjectChange(projectId);
        }
      });
    }
  }, [projectId, plugins]);

  return (
    <PluginsReadyContext.Provider value={pluginsReady}>
      {/* Render plugin onMount components for side effects */}
      {plugins.map((plugin) => {
        const OnMountComponent = plugin.onMount;
        return OnMountComponent ? <OnMountComponent key={plugin.id} /> : null;
      })}
      {children}
    </PluginsReadyContext.Provider>
  );
}


